import React, { useState, useRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import InputMC from './InputMC';
import PropertyListMC from './PropertyListMC';

/**
 * getCaretCoordinates - Calculates the top/left coordinates of the caret in a textarea/input.
 */
const getCaretCoordinates = (element, position) => {
  const div = document.createElement('div');
  const style = getComputedStyle(element);
  const properties = [
    'direction', 'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'paddingTop',
    'paddingRight', 'paddingBottom', 'paddingLeft', 'fontStyle', 'fontVariant', 'fontWeight',
    'fontStretch', 'fontSize', 'fontSizeAdjust', 'lineHeight', 'fontFamily', 'textAlign',
    'textTransform', 'textIndent', 'textDecoration', 'letterSpacing', 'wordSpacing',
    'tabSize', 'MozTabSize', 'whiteSpace', 'wordWrap', 'wordBreak'
  ];

  properties.forEach((prop) => {
    div.style[prop] = style[prop];
  });

  div.textContent = element.value.substring(0, position);
  const span = document.createElement('span');
  span.textContent = element.value.substring(position) || '.';
  div.appendChild(span);

  document.body.appendChild(div);

  div.style.position = 'absolute';
  div.style.top = '0px';
  div.style.left = '0px';
  div.style.visibility = 'hidden';

  const top = span.offsetTop + parseInt(style.borderTopWidth, 10) - element.scrollTop;
  const left = span.offsetLeft + parseInt(style.borderLeftWidth, 10) - element.scrollLeft;

  document.body.removeChild(div);

  return { top, left, lineHeight: parseInt(style.lineHeight, 10) };
};

/**
 * InputWithVariablesMC - Input component that supports inline variable insertion.
 */
const InputWithVariablesMC = React.forwardRef((props, ref) => {
  const {
    options,
    propertyListTitle,
    ...rest
  } = props;

  const { setValue } = useFormContext();
  const [showPropertyList, setShowPropertyList] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const getInputElement = () => {
    if (!containerRef.current) return null;
    return containerRef.current.querySelector('textarea, input');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const input = getInputElement();
      if (
        showPropertyList &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        input &&
        !input.contains(event.target)
      ) {
        setShowPropertyList(false);
      }
    };

    if (showPropertyList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPropertyList]);

  const handleInputChange = useCallback((e) => {
    const { value, selectionStart } = e.target;

    if (value[selectionStart - 1] === '@') {
      const { top, lineHeight } = getCaretCoordinates(e.target, selectionStart);
      // Align to the left (0 or input padding) instead of following the cursor horizontally
      setDropdownPosition({ top: top + (lineHeight || 20), left: 0 });
      setShowPropertyList(true);
    } else {
      setShowPropertyList(false);
    }

    if (rest.onChange) rest.onChange(e);
  }, [rest.onChange]);

  const handleInsertVariable = useCallback((variable) => {
    const input = getInputElement();
    if (!input) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;
    const text = input.value;
    const variableText = `{{${variable[1]}}}`;

    let newText;
    let newCursorPos;

    if (text[start - 1] === '@') {
      newText = text.substring(0, start - 1) + variableText + text.substring(end);
      newCursorPos = start - 1 + variableText.length;
    } else {
      newText = text.substring(0, start) + variableText + text.substring(end);
      newCursorPos = start + variableText.length;
    }

    setValue(rest.name, newText, { shouldDirty: true, shouldValidate: true });
    setShowPropertyList(false);

    setTimeout(() => {
      input.focus();
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [rest.name, setValue]);

  const triggerPropertyList = useCallback(() => {
    const input = getInputElement();
    if (!input) return;

    input.focus();
    // Use a small delay to ensure selectionStart is updated after focus
    setTimeout(() => {
      const { selectionStart } = input;
      const { top, lineHeight } = getCaretCoordinates(input, selectionStart);

      setDropdownPosition({ top: top + (lineHeight || 20), left: 0 });
      setShowPropertyList(true);
    }, 0);
  }, []);

  useImperativeHandle(ref, () => ({
    triggerPropertyList,
    handleInsertVariable
  }));

  return (
    <div className="tw-relative tw-w-full" ref={containerRef}>
      <InputMC
        {...rest}
        onInput={handleInputChange}
        onFocus={(e) => {
          if (rest.onFocus) rest.onFocus(e);
        }}
      />
      {showPropertyList && (
        <div ref={dropdownRef}>
          <PropertyListMC
            title={propertyListTitle || ''}
            properties={options}
            insertFieldInput={handleInsertVariable}
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              maxHeight: '200px',
              width: '300px',
              zIndex: 50
            }}
          />
        </div>
      )}
    </div>
  );
});

export default InputWithVariablesMC;
